import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Inquiry, InquiryStatus } from "@hunts/core";

import { api } from "../api";
import { PageHeader, Loading, ErrorNote, Empty, StatusPill, formatDate } from "../components/Page";

const STATUSES: InquiryStatus[] = ["NEW", "READ", "REPLIED", "ARCHIVED", "SPAM"];

export function Inbox() {
  const [filter, setFilter] = useState<InquiryStatus | "ALL">("ALL");
  const [openId, setOpenId] = useState<string | null>(null);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["inquiries"],
    queryFn: () => api.list<Inquiry>("inquiries"),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: InquiryStatus }) =>
      api.update("inquiries", id, { status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["inquiries"] });
      // The nav badge counts unread enquiries, so it has to refresh too.
      void qc.invalidateQueries({ queryKey: ["summary"] });
    },
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;

  const all = data ?? [];
  const items = filter === "ALL" ? all : all.filter((i) => i.status === filter);

  return (
    <>
      <PageHeader
        title="Inbox"
        count={items.length}
        actions={
          <select
            className="select"
            value={filter}
            onChange={(e) => setFilter(e.target.value as InquiryStatus | "ALL")}
          >
            <option value="ALL">All statuses</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.toLowerCase()}
              </option>
            ))}
          </select>
        }
      />

      {items.length === 0 ? (
        <Empty>
          <h2>Nothing here yet</h2>
          <p className="muted">
            Enquiries submitted through the contact form on huntscleaning.ca appear here.
          </p>
        </Empty>
      ) : (
        <ul className="cards">
          {items.map((inq) => {
            const open = openId === inq.id;
            return (
              <li key={inq.id} className={`card ${inq.status === "NEW" ? "card--unread" : ""}`}>
                <button
                  className="card__summary"
                  onClick={() => {
                    setOpenId(open ? null : inq.id);
                    // Opening an unread enquiry marks it read, so the badge
                    // reflects what actually still needs attention.
                    if (!open && inq.status === "NEW") {
                      setStatus.mutate({ id: inq.id, status: "READ" });
                    }
                  }}
                  aria-expanded={open}
                >
                  <span className="card__title">{inq.name}</span>
                  <span className="card__meta">
                    {inq.serviceType || "General enquiry"} · {formatDate(inq.createdAt)}
                  </span>
                  <StatusPill status={inq.status} />
                </button>

                {open && (
                  <div className="card__body">
                    <dl className="detail">
                      <div>
                        <dt>Email</dt>
                        <dd>
                          <a href={`mailto:${inq.email}`}>{inq.email}</a>
                        </dd>
                      </div>
                      {inq.phone && (
                        <div>
                          <dt>Phone</dt>
                          <dd>
                            <a href={`tel:${inq.phone.replace(/\D/g, "")}`}>{inq.phone}</a>
                          </dd>
                        </div>
                      )}
                    </dl>

                    <p className="message">{inq.message}</p>

                    <div className="card__actions">
                      {STATUSES.filter((s) => s !== inq.status).map((s) => (
                        <button
                          key={s}
                          className="btn btn--sm btn--ghost"
                          disabled={setStatus.isPending}
                          onClick={() => setStatus.mutate({ id: inq.id, status: s })}
                        >
                          Mark {s.toLowerCase()}
                        </button>
                      ))}
                      <a
                        className="btn btn--sm btn--primary"
                        href={`mailto:${inq.email}?subject=${encodeURIComponent("Re: your enquiry — Hunt's Cleaning Services")}`}
                      >
                        Reply
                      </a>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {setStatus.error && <ErrorNote error={setStatus.error} />}
    </>
  );
}
